// R6 — reading navigation. Tracks the current reading location from the
// retelling pane's scroll position, round-trips it through the `?loc=` URL param
// (replaceState on scroll, pushState on deliberate jumps — adapted from
// lekterion's `useReadingNav.writeUrl`), and restores position on open: `?loc=`
// wins, else whatever `restorePosition` reports. Location is tracked at block
// granularity (`{blockId, offset:0}`) — anchors are "rounded to block starts"
// and block-granular resume is stable against relayout.
//
// PERSISTENCE IS THE CALLER'S. This hook reads and writes `?loc=` (that is URL
// state, not persistence) and otherwise only reports through `onLocationChange`
// / asks through `restorePosition`. The reader wires both to the localStorage
// position record (resume-at-position, R7a); the `/admin` editor workbench wires
// neither, so scrolling a chapter to check an anchor cannot overwrite — or
// resume from — the admin's own reading position.
//
// The pure `serializeLocation`/`parseLocation` pair is unit-tested (the URL is an
// untrusted external boundary — `Number()` on a query param).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { RetellingLocation } from "@bindery/convex/model/location";
import type { DocumentModel } from "@bindery/shared/corpus/document";

const TRIGGER_RATIO = 0.24;

/** Serialize a location for the `?loc=` param: `b012` or `b012.34` (offset>0). */
export function serializeLocation(loc: RetellingLocation): string {
  return loc.offset > 0 ? `${loc.blockId}.${String(loc.offset)}` : loc.blockId;
}

/** Parse a `?loc=` param back to a location, or null when absent/malformed. */
export function parseLocation(raw: string | null): RetellingLocation | null {
  if (!raw) return null;
  const dot = raw.indexOf(".");
  if (dot === -1) return { blockId: raw, offset: 0 };
  const blockId = raw.slice(0, dot);
  const rest = raw.slice(dot + 1);
  const offset = Number(rest);
  // A single `blockId.offset` shape only: block ids carry no dots, so a second
  // dot (or a non-integer offset) is malformed.
  if (
    !blockId ||
    rest.includes(".") ||
    !Number.isInteger(offset) ||
    offset < 0
  ) {
    return null;
  }
  return { blockId, offset };
}

function writeLoc(loc: RetellingLocation, push: boolean): void {
  const url = new URL(window.location.href);
  url.searchParams.set("loc", serializeLocation(loc));
  const next = url.pathname + url.search + url.hash;
  if (push) window.history.pushState(null, "", next);
  else window.history.replaceState(null, "", next);
}

/** Content-space top of a block element within its scroll container. */
function blockTop(container: HTMLElement, el: HTMLElement): number {
  return (
    el.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop
  );
}

function scrollToLocation(container: HTMLElement, blockId: string): boolean {
  const el = container.querySelector<HTMLElement>(
    `[data-block-id="${blockId}"]`,
  );
  if (!el) return false;
  const target =
    blockTop(container, el) - container.clientHeight * TRIGGER_RATIO;
  // `behavior: "instant"` overrides CSS `scroll-behavior` so this doesn't
  // animate and spam intermediate scroll/restore events (see
  // use-sync-scroll's syncFrom for the same reasoning).
  container.scrollTo({ top: Math.max(0, target), behavior: "instant" });
  return true;
}

interface UseReadingNavArgs {
  retellingRef: RefObject<HTMLDivElement | null>;
  model: DocumentModel;
  /**
   * Identifies WHICH component renders the retelling column. `DocumentPane`
   * and `InterleavedColumn` sit at the same tree position, so switching
   * between them mounts a NEW node under the same ref; the scroll listener has
   * to move with it, or `?loc=`, resume and the bookmark button freeze for the
   * rest of the chapter. Narrower on purpose than the sync hooks' `layoutKey`:
   * those remeasure on font and mode changes too, this only needs re-binding
   * when the node itself is replaced.
   */
  columnKey: string;
  /**
   * The location changed — on a deliberate jump, or when scrolling crosses into
   * a new block. The reader saves it; omit it and nothing is persisted.
   *
   * Need NOT be stable: it is mirrored into a ref, because the scroll effect
   * below deliberately does not list it (adding it would rebind the listener on
   * every render) and an inline closure would otherwise go stale.
   */
  onLocationChange?: (location: RetellingLocation) => void;
  /**
   * Where to resume when there is no `?loc=`. Omit it and the column simply
   * opens at the top.
   *
   * Mirrored into a ref for the same reason as `onLocationChange`.
   */
  restorePosition?: () => RetellingLocation | null;
}

interface ReadingNav {
  /** The block nearest the reading line, or null before first measurement. */
  currentLocation: RetellingLocation | null;
  /** Jump to a location (bookmark/resume) — records a history entry. */
  jumpTo: (location: RetellingLocation) => void;
}

export function useReadingNav({
  retellingRef,
  model,
  columnKey,
  onLocationChange,
  restorePosition,
}: UseReadingNavArgs): ReadingNav {
  const [currentLocation, setCurrentLocation] =
    useState<RetellingLocation | null>(null);
  const currentRef = useRef<RetellingLocation | null>(null);

  // Keep the latest callbacks in refs so callers may pass inline closures
  // without the scroll effect rebinding every render — the same pattern
  // `use-copy-handler` uses for `onCopy` (useEffect discipline 4.3).
  const onLocationChangeRef = useRef(onLocationChange);
  const restorePositionRef = useRef(restorePosition);
  useEffect(() => {
    onLocationChangeRef.current = onLocationChange;
    restorePositionRef.current = restorePosition;
  });

  const jumpTo = useCallback(
    (location: RetellingLocation) => {
      const container = retellingRef.current;
      if (!container) return;
      if (scrollToLocation(container, location.blockId)) {
        currentRef.current = location;
        setCurrentLocation(location);
        writeLoc(location, true);
        onLocationChangeRef.current?.(location);
      }
    },
    [retellingRef],
  );

  useEffect(() => {
    const container = retellingRef.current;
    if (!container) return;

    const blockEls = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>("[data-block-id]"));

    // Track the block nearest above the reading line as the current location.
    const locationAtReadingLine = (): RetellingLocation | null => {
      const triggerY =
        container.getBoundingClientRect().top +
        container.clientHeight * TRIGGER_RATIO;
      let current: string | null = null;
      for (const el of blockEls()) {
        if (el.getBoundingClientRect().top <= triggerY) {
          current = el.dataset.blockId ?? current;
        } else break;
      }
      return current ? { blockId: current, offset: 0 } : null;
    };

    let frame = 0;
    // Set around `restore`'s own programmatic scroll so the resulting scroll
    // event isn't mistaken for the user's — otherwise the very first restore
    // would immediately mark `hasScrolled`, defeating the guard below.
    let restoring = false;
    // Once the user has genuinely scrolled, `restore` must never run again:
    // if fonts settle after they've already moved past the restored
    // position, re-running it would yank them back to `?loc=`/the saved spot.
    let hasScrolled = false;

    const trackLocation = (): void => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const loc = locationAtReadingLine();
        if (!loc) return;
        if (loc.blockId !== currentRef.current?.blockId) {
          currentRef.current = loc;
          setCurrentLocation(loc);
          writeLoc(loc, false);
          onLocationChangeRef.current?.(loc);
        }
      });
    };

    const onScroll = (): void => {
      if (restoring) {
        restoring = false;
      } else {
        hasScrolled = true;
      }
      trackLocation();
    };

    container.addEventListener("scroll", onScroll, { passive: true });

    // Restore once laid out: `?loc=` wins, else whatever the caller resumes
    // from. This also runs when `columnKey` swaps the column out from under us, and that
    // is what we want: the replacement lays the chapter out differently, and
    // `?loc=` is current (scroll tracking keeps writing it), so the reader
    // lands back at their place instead of at the top.
    const restore = (): void => {
      const fromUrl = parseLocation(
        new URLSearchParams(window.location.search).get("loc"),
      );
      const saved = restorePositionRef.current?.() ?? null;
      const target = fromUrl ?? saved;
      restoring = true;
      if (target && scrollToLocation(container, target.blockId)) {
        currentRef.current = target;
        setCurrentLocation(target);
      } else {
        // No scroll happened after all — nothing will clear the flag or
        // report a location, so do both ourselves.
        restoring = false;
        trackLocation();
      }
    };
    const initialFrame = requestAnimationFrame(restore);
    document.fonts?.ready
      .then(() => {
        if (!hasScrolled) restore();
      })
      .catch(() => undefined);

    return () => {
      container.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
      cancelAnimationFrame(initialFrame);
    };
    // `model` covers the chapter changing: a new chapter is a new
    // `DocumentModel` identity, so the listener re-binds even where the caller
    // is not remounted by key.
  }, [retellingRef, model, columnKey]);

  return { currentLocation, jumpTo };
}
